import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useAuthStore } from '../../stores/auth.store';
import { fmtEur } from '../../lib/utils';
import { isoDay, isoDayPlus, isSuperAdmin, type Entity } from '../../features/admin/adminApi';

/**
 * Fattura ricevuta inserita a mano (fornitore non ancora su FattureInCloud):
 * fornitore, numero, data, scadenza, totale. Salva con Ctrl/Cmd+Invio.
 */
export function NewPayableModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const [form, setForm] = useState({
    vendorName: '',
    number: '',
    issueDate: isoDay(),
    dueDate: isoDayPlus(30),
    total: '',
    entity: 'MASAMUNE' as Entity,
  });
  const total = Number(String(form.total).replace(',', '.'));

  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/admin/payables', {
      vendorName: form.vendorName.trim(),
      number: form.number.trim() || null,
      issueDate: form.issueDate,
      dueDate: form.dueDate || null,
      total,
      entity: form.entity,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin'] });
      toast.ok('Fattura ricevuta registrata', `${form.vendorName} · € ${fmtEur(total, true)}`);
      onClose();
    },
    onError: (err) => toast.alert('Non salvata', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const canSubmit = !!form.vendorName.trim() && Number.isFinite(total) && total > 0 && !!form.issueDate && !create.isPending;
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && canSubmit) { e.preventDefault(); create.mutate(); }
  };

  return (
    <Modal
      title="Fattura ricevuta"
      kicker="Amministrazione · Fornitori"
      onClose={onClose}
      size="md"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!canSubmit}>
            Registra <Icon name="arrowRight" size={12} />
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        <Field label="Fornitore" required span={12}>
          <input className="inp" autoFocus placeholder="Ragione sociale" value={form.vendorName} onChange={(e) => setForm({ ...form, vendorName: e.target.value })} />
        </Field>
        <Field label="Numero" span={6}>
          <input className="inp mono" placeholder="Es. 2026/0123" value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} />
        </Field>
        <Field label="Totale" required span={6} hint="IVA inclusa">
          <input className="inp mono" inputMode="decimal" placeholder="0,00" value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} />
        </Field>
        <Field label="Data" required span={6}>
          <input className="inp mono" type="date" value={form.issueDate} onChange={(e) => setForm({ ...form, issueDate: e.target.value })} />
        </Field>
        <Field label="Scadenza" span={6}>
          <input className="inp mono" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
        </Field>
        {isSuperAdmin(me) && (
          <Field label="Di chi" span={12}>
            <div className="seg-control">
              <button type="button" aria-pressed={form.entity === 'MASAMUNE'} onClick={() => setForm({ ...form, entity: 'MASAMUNE' })}>Masamune</button>
              <button type="button" aria-pressed={form.entity === 'PERSONALE_ANDREA'} onClick={() => setForm({ ...form, entity: 'PERSONALE_ANDREA' })}>Personale</button>
            </div>
          </Field>
        )}
      </div>
    </Modal>
  );
}
